import {
  API_BASE_URL,
  apiDelete,
  apiGet,
  apiGetOptional,
  apiPatch,
  apiPost,
  apiPut,
} from '@/api/http';

import type { AppNavigate } from '@/hooks/useUrlState';
import type {
  Client,
  ClientProfile,
  Company,
  CompanyActivity,
} from '@/types/company';
import type { User } from '@/types/user';
import type { CursorListingApiPayload } from '@/UI/filteredListing';
import type { AxiosRequestConfig } from 'axios';

type ClientPersonalPayload = {
  name?: string;
  title?: string;
  phone?: string;
  email?: string;
  department?: string;
  company?: string;
};

export type ClientCompanyPayload = {
  description?: string | null;
  difference_description?: string | null;
  company_culture?: string | null;
  excluded_companies?: string | null;
  notable_clients?: string | null;
  dress_code?: string | null;
  address?: string | null;
  number_of_employees?: number;
  average_age?: number;
  male_percentage?: number;
  city?: string;
  industry?: string;
  region?: string;
  name?: string;
  default_language?: string;
  image?: string;
  default_job_banner_image?: string;
  company_banner_image?: string;
};

export type ClientFormValues = ClientPersonalPayload &
  ClientCompanyPayload & {
    company_name?: string;
    company_id?: string;
    client_id?: string;
    office_locations?: { id: string; address: string }[] | null;
  };

export type ClientInvitationConfirmPayload = Record<string, unknown>;

export type ClientInviteResponse = {
  details?: string;
  detail?: string;
  message?: string;
  access_token?: string;
  user?: User;
};

export const getClientProfileList = (
  query?: string,
  config?: AxiosRequestConfig,
) =>
  apiGet<CursorListingApiPayload<ClientProfile>>(
    `${API_BASE_URL}clients/profiles/${query ?? ''}`,
    config,
  );

export const getClient = (id: string, navigate?: AppNavigate) =>
  apiGetOptional<Client>(`${API_BASE_URL}clients/${id}/`, undefined, navigate);

export const getCompanyActivityFeed = async (
  companyId: string,
): Promise<CompanyActivity[] | false> => {
  try {
    return await apiGet<CompanyActivity[]>(
      `${API_BASE_URL}client-companies/${companyId}/activity-feeds/`,
      { skipGlobalServerError: true },
    );
  } catch {
    return false;
  }
};

export const sendInvitationWithCreate = async (
  values: ClientFormValues,
): Promise<ClientInviteResponse | null> => {
  const clientData: ClientPersonalPayload = {
    name: values.name,
    title: values.title,
    phone: values.phone,
    email: values.email,
    department: values.department,
    company: values.company,
  };
  const companyData: ClientCompanyPayload = {
    description: values.description,
    difference_description: values.difference_description,
    address: values.address,
    notable_clients: values.notable_clients,
    number_of_employees: values.number_of_employees,
    average_age: values.average_age,
    male_percentage: values.male_percentage,
    dress_code: values.dress_code,
    city: values.city,
    industry: values.industry,
    region: values.region,
    name: values.company_name,
  };

  try {
    const company = values.company
      ? await editClientCompany(companyData, values.company)
      : await createClientCompany(companyData);
    const invitation = await sendClientInvitation({
      ...clientData,
      company: company.id,
    });
    if (values.office_locations) {
      await createClientAddressList(company.id, values.office_locations);
    }
    return invitation;
  } catch {
    return null;
  }
};

export const editClient = async (
  values: ClientFormValues,
): Promise<({ company: Company } & ClientInviteResponse) | null> => {
  const clientData: ClientPersonalPayload = {
    name: values.name,
    title: values.title,
    phone: values.phone,
    department: values.department,
  };
  const companyData: ClientCompanyPayload = {
    description: values.description,
    difference_description: values.difference_description,
    address: values.address,
    notable_clients: values.notable_clients,
    number_of_employees: values.number_of_employees,
    average_age: values.average_age,
    male_percentage: values.male_percentage,
    dress_code: values.dress_code,
    city: values.city,
    industry: values.industry,
    region: values.region,
    name: values.company_name,
  };
  try {
    if (!values.company_id || !values.client_id) {
      throw new Error('Missing company_id or client_id');
    }
    const company = await editClientCompany(companyData, values.company_id);
    const invitation = await editClientInfo(clientData, values.client_id);
    if (values.office_locations) {
      await createClientAddressList(company.id, values.office_locations);
    }
    return { ...invitation, company };
  } catch {
    return null;
  }
};

const sendClientInvitation = (info: ClientPersonalPayload) =>
  apiPost<ClientInviteResponse>(`${API_BASE_URL}clients/invite/`, info);

export const updateClient = (info: Record<string, unknown>) =>
  apiPatch<Client>(`${API_BASE_URL}clients/me/`, info);

const editClientInfo = (info: ClientPersonalPayload, id: string) =>
  apiPut<Client>(`${API_BASE_URL}clients/${id}/`, info);

export const confirmClientInvitation = (
  info: ClientInvitationConfirmPayload,
  profileId?: string,
) => {
  const url = profileId
    ? `${API_BASE_URL}users/invite/confirm/?profile_id=${profileId}`
    : `${API_BASE_URL}users/invite/confirm/`;
  return apiPost<ClientInviteResponse>(url, info);
};

const createClientCompany = (data: ClientCompanyPayload) =>
  apiPost<Company>(`${API_BASE_URL}client-companies/`, data);

export const editClientCompany = (
  data: ClientCompanyPayload,
  company_id: string,
) => apiPatch<Company>(`${API_BASE_URL}client-companies/${company_id}/`, data);

const createClientAddress = (
  company_id: string,
  address: { address: string },
) =>
  apiPost<{ id: string; address: string }>(
    `${API_BASE_URL}client-companies/${company_id}/office-locations/`,
    address,
  );

const editClientAddress = (
  company_id: string,
  locId: string,
  address: { address: string },
) =>
  apiPatch<{ id: string; address: string }>(
    `${API_BASE_URL}client-companies/${company_id}/office-locations/${locId}/`,
    address,
  );

export const deleteClientLocation = (id: string, company_id: string) =>
  apiDelete(
    `${API_BASE_URL}client-companies/${company_id}/office-locations/${id}/`,
  );

export const createClientAddressList = (
  company_id: string,
  list: { id: string; address: string }[],
) => {
  const addresses = list
    .filter((el) => el.address?.trim())
    .map((el) => {
      if (el.id) {
        return editClientAddress(company_id, el.id, { address: el.address });
      }
      return createClientAddress(company_id, { address: el.address });
    });

  if (addresses.length === 0) {
    return Promise.resolve([]);
  }

  return Promise.all(addresses);
};
